import {
  WorkflowInstructionAsset,
  WorkflowInstructionAssetKind,
  WorkflowInstructionAssetReadUrl,
  WorkflowInstructionAssetApi,
  toAsset,
  readString,
  requireString,
} from '@shared/workflows/authoring';
export {
  WorkflowInstructionAsset,
  WorkflowInstructionAssetKind,
  WorkflowInstructionAssetReadUrl,
  WorkflowInstructionAssetApi,
  toAsset,
  readString,
  requireString,
  WORKFLOW_INSTRUCTION_ASSET_API,
} from '@shared/workflows/authoring';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, map, switchMap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { uploadAzureBlob } from '@shared/core/blob-upload';
import {
  type CreateWorkflowInstructionAssetRequest,
  WorkflowDefinitionsService,
} from '../../../../api/gen';

@Injectable({ providedIn: 'root' })
export class GeneratedWorkflowInstructionAssetApi implements WorkflowInstructionAssetApi {
  private readonly api = inject(WorkflowDefinitionsService);

  list(slug: string): Observable<readonly WorkflowInstructionAsset[]> {
    return this.api
      .v1WorkflowsSlugChecklistAssetsGet(slug)
      .pipe(map((assets) => assets.map(toAsset)));
  }

  upload(
    slug: string | undefined,
    file: File,
    kind: WorkflowInstructionAssetKind,
    onProgress: (loadedBytes: number) => void,
  ): Observable<WorkflowInstructionAsset> {
    const request = {
      fileName: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    };
    const created = !slug
      ? this.api.v1WorkflowsUnassignedAssetsPost({
          ...request,
          kind:
            kind === 'audio' ? 'Audio' : kind === 'image' ? 'Image' : 'Video',
        })
      : kind === 'audio'
        ? this.api.v1WorkflowsSlugChecklistAssetsPost(slug, {
            ...request,
            kind: 'Audio',
          })
        : this.api.v1WorkflowsSlugInstructionAssetsPost(slug, {
            ...request,
            kind: kind === 'image' ? 'Image' : 'Video',
          } satisfies CreateWorkflowInstructionAssetRequest);
    return created.pipe(
      switchMap((created) => {
        const asset = toAsset(created);
        const uploadSasUri = readString(created, 'uploadSasUri');
        if (!uploadSasUri)
          return throwError(
            () => new Error('The upload URL was not returned.'),
          );
        return uploadAzureBlob(uploadSasUri, file, { onProgress }).pipe(
          switchMap(() =>
            !slug
              ? this.api.v1WorkflowsUnassignedAssetsAssetIdCompletePost(
                  asset.id,
                )
              : kind === 'audio'
                ? this.api.v1WorkflowsSlugChecklistAssetsAssetIdCompletePost(
                    slug,
                    asset.id,
                  )
                : this.api.v1WorkflowsSlugInstructionAssetsAssetIdCompletePost(
                    slug,
                    asset.id,
                  ),
          ),
          map(toAsset),
        );
      }),
    );
  }

  readUrl(
    slug: string | undefined,
    assetId: string,
  ): Observable<WorkflowInstructionAssetReadUrl> {
    const unassigned = () =>
      this.api.v1WorkflowsUnassignedAssetsAssetIdReadUrlGet(assetId);
    const read = slug
      ? this.api
          .v1WorkflowsSlugInstructionAssetsAssetIdReadUrlGet(slug, assetId)
          .pipe(
            catchError((error: unknown) =>
              error instanceof HttpErrorResponse && error.status === 404
                ? unassigned()
                : throwError(() => error),
            ),
          )
      : unassigned();
    return read.pipe(
      map((value) => ({
        url: requireString(value, 'url'),
        expiresAt: requireString(value, 'expiresAt'),
      })),
    );
  }
}
